import { Fallback, Image, Root } from '@radix-ui/react-avatar';
import cx from 'clsx';
import type { TypeVariant } from 'src/components/core';
import { Type } from 'src/components/core';
import type {
  ClassNames,
  HeightClassName,
  WidthClassName,
} from 'src/types/classNames';
import { getInitials } from 'src/utils/string';

type AvatarSize = 'xsmall' | 'small' | 'medium' | 'large';

export interface AvatarProps {
  className?: string;
  fallbackClassName?: string;

  /**
   * @default 'medium'
   */
  size?: AvatarSize;
  user: {
    title: string;
    content: string;
    publishedAt: string;
    slug: string;
    author: string;
    image: {
      data?: {
        attributes: {
          url: string;
        };
      };
    };
    author_image: {
      data?: {
        attributes: {
          url: string;
        };
      };
    };
  };
}

const SizeClassNames: Record<
  AvatarSize,
  ClassNames<HeightClassName | WidthClassName>
> = {
  xsmall: ['h-7', 'w-7'],
  small: ['h-9', 'w-9'],
  medium: ['h-14', 'w-14'],
  large: ['h-24', 'w-24'],
};

const FallbackTypeVariants: Record<AvatarSize, TypeVariant> = {
  xsmall: 'text-sm',
  small: 'text-sm',
  medium: 'text-base',
  large: 'text-lg',
};

export function Avatar({ className, size = 'medium', user }: AvatarProps) {
  const alt = user?.author ?? 'avatar';
  return (
    <Root
      className={cx(
        'flex items-center justify-center overflow-hidden rounded-full border border-gray-200 border-opacity-20',
        SizeClassNames[size],
        className
      )}
    >
      {user?.author_image?.data?.attributes?.url ? (
        <Image alt={alt} src={user?.author_image?.data?.attributes?.url} />
      ) : (
        <Image alt={alt} src={'/images/antoine-bello-avatar.jpeg'} />
      )}
      <Fallback asChild>
        <Type variant={FallbackTypeVariants[size]}>{getInitials(alt)}</Type>
      </Fallback>
    </Root>
  );
}
